/**
 * External dependencies
 */
import { jsPDF } from 'jspdf';

/**
 * PDF Generator Class for WordPress Schedule/Programme content
 */
export class SchedulePDFGenerator {
	/**
	 * Default configuration options
	 */
	static DEFAULT_CONFIG = {
		// Page settings
		margin: 15,
		lineHeight: 7,
		pageBreakThreshold: 270,

		// Font sizes
		fontSize: {
			title: 16,
			dayHeader: 14,
			sectionHeader: 11,
			sessionHeader: 10,
			body: 10,
		},

		// Colors (RGB)
		colors: {
			dayHeaderBg: [41, 128, 185],
			dayHeaderText: [255, 255, 255],
			bodyText: [0, 0, 0],
		},

		// Spacing
		spacing: {
			afterTitle: 10,
			afterDayHeader: 15,
			beforeDayHeader: 5,
			afterSectionHeader: 4,
			afterSessionHeader: 3,
			afterParagraph: 3,
			afterList: 2,
			betweenDays: 5,
		},

		// Content settings
		bulletIndent: 5,
		bulletTextIndent: 10,
		defaultTitle: 'Course Programme',
		titleAttribute: 'data-schedule-title',

		// Filename settings
		includeTimestamp: true,
		dateFormat: 'YYYY-MM-DD',
	};

	/**
	 * Constructor
	 * @param {Object} config - Configuration options to override defaults
	 */
	constructor(config = {}) {
		this.config = this.mergeConfig(
			SchedulePDFGenerator.DEFAULT_CONFIG,
			config
		);
		this.doc = null;
		this.yPos = 20;
	}

	/**
	 * Deep merge configuration objects
	 */
	mergeConfig(defaults, custom) {
		const merged = { ...defaults };

		for (const key in custom) {
			if (
				custom[key] &&
				typeof custom[key] === 'object' &&
				!Array.isArray(custom[key])
			) {
				merged[key] = { ...defaults[key], ...custom[key] };
			} else {
				merged[key] = custom[key];
			}
		}

		return merged;
	}

	/**
	 * Initialize a new PDF document
	 */
	initializeDocument() {
		this.doc = new jsPDF();
		this.yPos = 20;
	}

	/**
	 * Get page dimensions
	 */
	getPageDimensions() {
		const pageWidth = this.doc.internal.pageSize.getWidth();
		const maxWidth = pageWidth - this.config.margin * 2;
		return { pageWidth, maxWidth };
	}

	/**
	 * Check if a page break is needed
	 * @param {number} neededSpace - Space needed for next content
	 * @returns {boolean} - True if page was added
	 */
	checkPageBreak(neededSpace = 20) {
		if (this.yPos + neededSpace > this.config.pageBreakThreshold) {
			this.doc.addPage();
			this.yPos = 20;
			return true;
		}
		return false;
	}

	/**
	 * Add page heading (title)
	 * @param {string} title - The title text
	 */
	addPageHeading(title) {
		this.checkPageBreak(15);
		this.doc.setFontSize(this.config.fontSize.title);
		this.doc.setFont(undefined, 'bold');
		this.doc.text(title, this.config.margin, this.yPos);
		this.yPos += this.config.spacing.afterTitle;
	}

	/**
	 * Extract text from element while preserving line breaks
	 * @param {HTMLElement} element - The element to extract text from
	 * @returns {string} - Text with preserved line breaks
	 */
	extractTextWithLineBreaks(element) {
		const clone = element.cloneNode(true);
		const brTags = clone.querySelectorAll('br');

		brTags.forEach((br) => {
			br.replaceWith('\n');
		});

		return clone.textContent.trim();
	}

	/**
	 * Add text with word wrap
	 * @param {string} text - Text to add
	 * @param {number} fontSize - Font size
	 * @param {string} fontStyle - Font style (normal, bold, italic)
	 * @param {number} extraSpace - Extra vertical space after text
	 * @param {number} indent - Left indent
	 */
	addText(text, fontSize, fontStyle, extraSpace = 0, indent = 0) {
		this.checkPageBreak();
		this.doc.setFontSize(fontSize);
		this.doc.setFont(undefined, fontStyle);

		const { maxWidth } = this.getPageDimensions();

		// Split by line breaks and render each line directly
		const paragraphs = text.split('\n').filter((p) => p.trim());
		paragraphs.forEach((paragraph) => {
			const lines = this.doc.splitTextToSize(
				paragraph,
				maxWidth - indent
			);
			this.doc.text(lines, this.config.margin + indent, this.yPos);
			this.yPos += lines.length * this.config.lineHeight;
		});

		this.yPos += extraSpace;
	}

	/**
	 * Add a day header with background
	 * @param {string} text - Header text
	 */
	addDayHeader(text) {
		this.checkPageBreak(15);
		const { maxWidth } = this.getPageDimensions();

		// Background
		this.doc.setFillColor(...this.config.colors.dayHeaderBg);
		this.doc.rect(
			this.config.margin,
			this.yPos - this.config.spacing.beforeDayHeader,
			maxWidth,
			10,
			'F'
		);

		// Text
		this.doc.setTextColor(...this.config.colors.dayHeaderText);
		this.doc.setFontSize(this.config.fontSize.dayHeader);
		this.doc.setFont(undefined, 'bold');
		this.doc.text(text, this.config.margin + 2, this.yPos + 2);

		// Reset text color
		this.doc.setTextColor(...this.config.colors.bodyText);
		this.yPos += this.config.spacing.afterDayHeader;
	}

	/**
	 * Add a bullet point
	 * @param {string} text - Bullet text
	 */
	addBullet(text) {
		this.checkPageBreak();
		this.doc.setFontSize(this.config.fontSize.body);
		this.doc.setFont(undefined, 'normal');

		const { maxWidth } = this.getPageDimensions();
		const bulletX = this.config.margin + this.config.bulletIndent;
		const textX = this.config.margin + this.config.bulletTextIndent;

		this.doc.text('•', bulletX, this.yPos);

		// Handle line breaks in bullet text - render directly
		const paragraphs = text.split('\n').filter((p) => p.trim());
		paragraphs.forEach((paragraph) => {
			const lines = this.doc.splitTextToSize(
				paragraph,
				maxWidth - this.config.bulletTextIndent
			);
			this.doc.text(lines, textX, this.yPos);
			this.yPos += lines.length * this.config.lineHeight;
		});

		this.yPos += 1;
	}

	/**
	 * Extract title from article element
	 * @param {HTMLElement} panel - The panel element
	 * @returns {string} - The title
	 */
	extractTitle(panel) {
		// Try to find title from aria-labelledby (tab title)
		const labelledBy = panel.getAttribute('aria-labelledby');
		if (labelledBy) {
			const labelElement = document.getElementById(labelledBy);
			if (labelElement && labelElement.textContent.trim()) {
				return labelElement.textContent.trim();
			}
		}

		const mainArticle = document.querySelector('main > article');
		return mainArticle
			? mainArticle.getAttribute(this.config.titleAttribute) ||
					this.config.defaultTitle
			: this.config.defaultTitle;
	}

	/**
	 * Process provisional programme paragraph
	 * @param {HTMLElement} panel - The panel element
	 */
	processProvisionalParagraph(panel) {
		const provPara = panel.querySelector('p > strong');
		if (provPara && provPara.parentElement) {
			const text = this.extractTextWithLineBreaks(provPara.parentElement);
			this.addText(text, this.config.fontSize.body, 'italic', 10);
		}
	}

	/**
	 * Determine paragraph type and styling
	 * @param {HTMLElement} element - The paragraph element
	 * @returns {Object} - Style information
	 */
	analyzeParagraph(element) {
		const strong = element.querySelector('strong');
		const text = this.extractTextWithLineBreaks(element);

		if (!strong) {
			return {
				type: 'regular',
				fontSize: this.config.fontSize.body,
				fontStyle: 'normal',
				spacing: this.config.spacing.afterParagraph,
			};
		}

		const strongText = strong.textContent.trim();

		// Check if it's a time-based session header (e.g., "9:00" or "14.30")
		if (/^\d{1,2}[:.]?\d{0,2}/.test(strongText)) {
			return {
				type: 'session',
				fontSize: this.config.fontSize.sessionHeader,
				fontStyle: 'bold',
				spacing: this.config.spacing.afterSessionHeader,
			};
		}

		// Otherwise it's a section header
		return {
			type: 'section',
			fontSize: this.config.fontSize.sectionHeader,
			fontStyle: 'bold',
			spacing: this.config.spacing.afterSectionHeader,
		};
	}

	/**
	 * Process a details section (day)
	 * @param {HTMLElement} detail - The details element
	 */
	processDetailsSection(detail) {
		// Get day header from summary
		const summary = detail.querySelector('.details__heading');
		if (summary) {
			const dayText = summary.textContent.trim();
			this.addDayHeader(dayText);
		}

		// Get content wrapper
		const wrapper = detail.querySelector('.details__wrapper');
		if (!wrapper) return;

		// Process all children
		const children = wrapper.children;
		for (let i = 0; i < children.length; i++) {
			const child = children[i];
			const tagName = child.tagName.toLowerCase();
			const text = this.extractTextWithLineBreaks(child);

			if (!text) continue;

			if (tagName === 'p') {
				const style = this.analyzeParagraph(child);
				this.addText(
					text,
					style.fontSize,
					style.fontStyle,
					style.spacing
				);
			} else if (tagName === 'ul') {
				this.processListItems(child);
				this.yPos += this.config.spacing.afterList;
			}
		}
	}

	/**
	 * Process list items
	 * @param {HTMLElement} listElement - The UL element
	 */
	processListItems(listElement) {
		const listItems = listElement.querySelectorAll('li');
		listItems.forEach((li) => {
			const liText = this.extractTextWithLineBreaks(li);
			if (liText) {
				this.addBullet(liText);
			}
		});
	}

	/**
	 * Generate filename for the PDF
	 * @param {string} title - The document title
	 * @returns {string} - Formatted filename
	 */
	generateFilename(title) {
		const baseFilename = title.toLowerCase().replace(/\s+/g, '-');

		if (!this.config.includeTimestamp) {
			return `${baseFilename}.pdf`;
		}

		const timestamp = new Date().toISOString().split('T')[0];
		return `${baseFilename}-${timestamp}.pdf`;
	}

	/**
	 * Main conversion method
	 * @param {HTMLElement} panel - The panel element containing schedule content
	 * @returns {Promise<void>}
	 */
	async convert(panel) {
		try {
			if (!panel) {
				throw new Error('Panel element not found');
			}

			// Initialize document
			this.initializeDocument();

			// Extract and add title
			const title = this.extractTitle(panel);
			this.addPageHeading(title);

			// Process provisional programme paragraph
			this.processProvisionalParagraph(panel);

			// Process each details section (day)
			const details = panel.querySelectorAll('details');
			details.forEach((detail, index) => {
				this.processDetailsSection(detail);

				// Add spacing between days
				if (index < details.length - 1) {
					this.yPos += this.config.spacing.betweenDays;
				}
			});

			// Save the PDF
			const filename = this.generateFilename(title);
			this.doc.save(filename);
		} catch (err) {
			console.error('Error generating PDF:', err);
			throw err;
		}
	}
}
